import { useEffect, useMemo, useRef, useState } from 'react';
import { Bot, Check, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useTheme } from 'components/core/theme-provider';
import { Dialog, DialogContent, DialogTitle } from 'components/ui/dialog';
import { Skeleton } from 'components/ui/skeleton';
import { cn } from 'lib/utils';
import { useDebounce } from 'hooks/use-debounce';
import type { AgentSummary } from 'features/chat/agent-chat-module/types/agent.service.type';
import { useGetAgentsInfinite } from 'features/chat/hooks/use-agents';
import { getPickerTheme } from 'pages/agents/utils/agent-config-picker-theme';

const MAX_SUBAGENTS = 10;

type AgentConfigAddSubagentModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agentId: string;
  projectKey: string;
  selectedSubagentIds: string[];
  onAddSubagent: (agent: AgentSummary) => void;
  onRemoveSubagent: (subagentId: string) => void;
};

function SubagentRowSkeleton({ fieldClass }: { fieldClass: string }) {
  return (
    <div className={cn('flex items-center gap-3 rounded-2xl border p-4', fieldClass)}>
      <Skeleton className="h-11 w-11 shrink-0 rounded-2xl" />
      <div className="min-w-0 flex-1 space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-48" />
      </div>
      <Skeleton className="h-8 w-16 rounded-full" />
    </div>
  );
}

export function AgentConfigAddSubagentModal({
  open,
  onOpenChange,
  agentId,
  projectKey,
  selectedSubagentIds,
  onAddSubagent,
  onRemoveSubagent,
}: AgentConfigAddSubagentModalProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const ui = getPickerTheme(theme === 'dark');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const trimmedSearch = debouncedSearch.trim();

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetAgentsInfinite(
    {
      subagentEligible: true,
      excludeAgentId: agentId,
      limit: 20,
      project_key: projectKey,
      show_hidden_agent: false,
      ...(trimmedSearch ? { name: trimmedSearch } : {}),
    },
    { enabled: open && !!projectKey && !!agentId, scope: 'subagent-picker' }
  );

  const agents = useMemo(
    () => data?.pages.flatMap((page) => page.agents) ?? [],
    [data?.pages]
  );
  const selectedAgents = agents.filter((agent) => selectedSubagentIds.includes(agent.id));
  const selectedCount = selectedSubagentIds.length;
  const atMaxSubagents = selectedCount >= MAX_SUBAGENTS;

  useEffect(() => {
    const scrollContainer = listContainerRef.current;
    const loadMoreEl = loadMoreRef.current;
    if (!open || !scrollContainer || !loadMoreEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        root: scrollContainer,
        rootMargin: '200px',
        threshold: 0.1,
      }
    );

    observer.observe(loadMoreEl);
    return () => observer.disconnect();
  }, [open, hasNextPage, isFetchingNextPage, fetchNextPage, agents.length, isLoading]);

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      setSearchQuery('');
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        hideClose
        className={cn(
          'flex h-[min(92dvh,720px)] w-[calc(100vw-2rem)] max-w-[640px] flex-col gap-0 overflow-hidden rounded-3xl p-0 shadow-2xl',
          ui.dialog
        )}
      >
        <DialogTitle className="sr-only">{t('ADD_SUBAGENT') || 'Add subagent'}</DialogTitle>

        <div className={cn('relative shrink-0 px-6 pb-5 pt-6', ui.header)}>
          <button
            type="button"
            onClick={() => handleClose(false)}
            className={cn('absolute right-4 top-4 rounded-full p-1.5 transition-colors', ui.close)}
            aria-label={t('CLOSE', 'Close')}
          >
            <X className="h-4 w-4" />
          </button>

          <p className={cn('pr-8 font-sans text-[1.65rem] font-bold leading-tight', ui.title)}>
            {t('ADD_SUBAGENT') || 'Add subagent'}
          </p>
          <p className={cn('mt-2 pr-8 font-sans text-sm leading-5', ui.subtitle)}>
            {t('BROWSE_AND_SELECT_SUBAGENTS_FOR_AGENT') ||
              'Browse and select eligible agents to attach as subagents.'}
          </p>

          <div className="relative mt-5">
            <Search
              className={cn(
                'pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2',
                ui.searchIcon
              )}
            />
            <input
              type="search"
              placeholder={t('SEARCH_AGENTS') || 'Search agents...'}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className={cn(
                'h-12 w-full rounded-2xl border pl-11 pr-4 text-sm outline-none transition-colors',
                ui.search
              )}
            />
          </div>
        </div>

        {selectedAgents.length > 0 && (
          <div className={cn('flex shrink-0 flex-wrap gap-2 border-b px-6 pb-4 pt-4', ui.chipWrap)}>
            {selectedAgents.map((agent) => (
              <button
                key={agent.id}
                type="button"
                onClick={() => onRemoveSubagent(agent.id)}
                className={cn(
                  'inline-flex max-w-full items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
                  ui.chip
                )}
              >
                <span className="truncate">{agent.name}</span>
                <X className="h-3 w-3 shrink-0" />
              </button>
            ))}
          </div>
        )}

        <div
          ref={listContainerRef}
          className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain px-6 py-4"
        >
          {isLoading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <SubagentRowSkeleton key={index} fieldClass={ui.field} />
            ))
          ) : agents.length === 0 ? (
            <div className="flex h-full min-h-[220px] flex-col items-center justify-center text-center">
              <div
                className={cn(
                  'mb-3 flex h-14 w-14 items-center justify-center rounded-full',
                  ui.emptyIcon
                )}
              >
                <Bot className="h-6 w-6" />
              </div>
              <p className={cn('text-sm font-medium', ui.body)}>
                {searchQuery
                  ? t('NO_AGENTS_FOUND_MATCHING_SEARCH') || 'No agents found matching your search'
                  : t('NO_SUBAGENTS_AVAILABLE') || 'No eligible subagents available'}
              </p>
            </div>
          ) : (
            <>
              {agents.map((agent) => {
                const isSelected = selectedSubagentIds.includes(agent.id);
                const addDisabled = atMaxSubagents && !isSelected;

                return (
                  <button
                    key={agent.id}
                    type="button"
                    disabled={addDisabled}
                    onClick={() =>
                      isSelected ? onRemoveSubagent(agent.id) : onAddSubagent(agent)
                    }
                    className={cn(
                      'flex w-full items-center gap-3 rounded-2xl border px-4 py-3.5 text-left transition-colors',
                      isSelected ? ui.rowSelected : ui.rowIdle,
                      addDisabled && 'cursor-not-allowed opacity-50'
                    )}
                  >
                    {agent.logo_url ? (
                      <img
                        src={agent.logo_url}
                        alt=""
                        className="h-11 w-11 shrink-0 rounded-2xl object-cover"
                      />
                    ) : (
                      <div
                        className={cn(
                          'flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl',
                          isSelected ? ui.iconSelected : ui.iconIdle
                        )}
                      >
                        <Bot className="h-5 w-5" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className={cn('truncate font-medium', ui.body)}>{agent.name}</div>
                      <div className={cn('line-clamp-2 break-words text-sm', ui.muted)}>
                        {agent.description ||
                          agent.role ||
                          t('NO_DESCRIPTION') ||
                          'No description'}
                      </div>
                    </div>
                    <span
                      className={cn(
                        'inline-flex h-8 shrink-0 items-center gap-1 rounded-full px-3 text-xs font-semibold',
                        isSelected ? ui.pillRemove : ui.pillIdle
                      )}
                    >
                      {isSelected ? <Check className="h-3.5 w-3.5" /> : null}
                      {isSelected ? t('REMOVE') || 'Remove' : t('ADD') || 'Add'}
                    </span>
                  </button>
                );
              })}

              {isFetchingNextPage &&
                Array.from({ length: 2 }).map((_, index) => (
                  <SubagentRowSkeleton key={`subagent-load-more-${index}`} fieldClass={ui.field} />
                ))}

              {hasNextPage && (
                <div ref={loadMoreRef} className="h-1 w-full shrink-0" aria-hidden />
              )}
            </>
          )}
        </div>

        <div className={cn('shrink-0 border-t px-6 py-4', ui.footer)}>
          <p className={cn('text-sm', ui.muted)}>
            {selectedCount}/{MAX_SUBAGENTS}{' '}
            {selectedCount === 1
              ? t('SUBAGENT_SELECTED') || 'subagent selected'
              : t('SUBAGENTS_SELECTED') || 'subagents selected'}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
